// Center-crosshair reflectivity reticle ("Tähtäin").
//
// A RadarScope-style reticle pinned to the *center* of the map viewport. Unlike
// the Mittaa / Pistemittaus map-tap tools, this one never moves — the user pans
// the map underneath it to change the measured point. It shows, at screen
// center:
//   - full-viewport vertical + horizontal crosshair lines,
//   - a small center ring filled with the *actual rendered radar pixel colour*
//     at that point (so the user sees exactly which value the readout samples —
//     the ring spans several pixels, but the fill is the single centre pixel),
//   - two outer aiming rings (fixed pixel radii, zoom-independent),
//   - a line from the centre toward the selected (or nearest) radar site,
//   - the dBZ value at the centre, below the crosshair.
//
// The dBZ value reuses the exact EDR position query Pistemittaus uses (shared
// helpers in probe.js) so the two readouts agree at the same coordinate. The
// pixel colour is read straight off the radar layer's canvas via getData() —
// which requires the radar ImageWMS source to be crossOrigin-enabled.

import { transform } from 'ol/proj';
import { getDistance } from 'ol/sphere';
import {
  resolveEdrTarget, fetchSeries, normalizeLonLat, paramSpec, formatReadout,
} from './probe';
import { FRAME_COUNT } from './constants';
import { peaksByFrame, frameIndexAt } from './edr/peaks';
import {
  frameWindow, sameWindow, sameTarget, createFetchSlot,
} from './edr/seriesFetch';

const SVG_NS = 'http://www.w3.org/2000/svg';

// Reticle geometry (SVG user units == CSS px). Center ring is the colour
// swatch; the two outer rings are fixed-size aiming circles. The radar line
// starts just outside the centre ring.
const SVG_SIZE = 320;
const C = SVG_SIZE / 2;
const R_CENTER = 10;
const R_MID = 38;
const R_OUTER = 74;
const LINE_INNER = R_CENTER + 4;
// Cap the arrow length for far radars. For nearer ones the arrow is drawn only
// as long as the radar's on-screen distance, so the head lands on the radar
// instead of overshooting past it (see updateDirection).
const LINE_OUTER_MAX = 150;
const ARROW_LEN = 10;
const ARROW_HALF_W = 5;

// Hide the radar arrow when the radar sits essentially under the reticle — too
// close on screen to draw a sensible arrow.
const MIN_TARGET_PX = LINE_INNER + ARROW_LEN;

function el(tag, attrs) {
  const node = document.createElementNS(SVG_NS, tag);
  Object.keys(attrs).forEach((k) => node.setAttribute(k, attrs[k]));
  return node;
}

export default function initCrosshair({
  map, radarLayer, radarSiteSource, getActiveSiteLonLat,
}) {
  // --- DOM -----------------------------------------------------------------
  const overlay = document.createElement('div');
  overlay.className = 'crosshair-overlay';
  overlay.setAttribute('aria-hidden', 'true');
  overlay.style.display = 'none';

  // Full-viewport crosshair lines (kept out of the bounded SVG so they span the
  // whole map, not just the reticle).
  const hLine = document.createElement('div');
  hLine.className = 'crosshair-line crosshair-line-h';
  const vLine = document.createElement('div');
  vLine.className = 'crosshair-line crosshair-line-v';
  overlay.appendChild(hLine);
  overlay.appendChild(vLine);

  const svg = el('svg', {
    class: 'crosshair-svg',
    width: SVG_SIZE,
    height: SVG_SIZE,
    viewBox: `0 0 ${SVG_SIZE} ${SVG_SIZE}`,
  });

  // Radar-direction line + arrowhead. Endpoints are recomputed in screen space
  // each render (updateDirection), so no rotate() transform is needed.
  const dirGroup = el('g', { class: 'crosshair-dir' });
  const dirLine = el('line', { class: 'crosshair-dir-line' });
  const dirArrow = el('polygon', { class: 'crosshair-dir-arrow' });
  dirGroup.appendChild(dirLine);
  dirGroup.appendChild(dirArrow);
  svg.appendChild(dirGroup);

  svg.appendChild(el('circle', {
    cx: C, cy: C, r: R_OUTER, class: 'crosshair-ring',
  }));
  svg.appendChild(el('circle', {
    cx: C, cy: C, r: R_MID, class: 'crosshair-ring',
  }));
  const centerCircle = el('circle', {
    cx: C, cy: C, r: R_CENTER, class: 'crosshair-center empty',
  });
  svg.appendChild(centerCircle);
  overlay.appendChild(svg);

  const readout = document.createElement('div');
  readout.className = 'crosshair-readout';
  readout.hidden = true;
  overlay.appendChild(readout);

  // Insert below OL's overlay containers (the radar-site / marker cards) so
  // those still render on top of the reticle, but above the map canvas — DOM
  // order handles the stacking. Fall back to append if the container is absent.
  const viewport = map.getViewport();
  const olOverlays = viewport.querySelector('.ol-overlaycontainer-stopevent');
  if (olOverlays) viewport.insertBefore(overlay, olOverlays);
  else viewport.appendChild(overlay);

  // --- state ---------------------------------------------------------------
  let visible = false;
  let collection = null;
  let parameter = null;
  let z = null;
  let center4326 = null; // normalized [lon, lat]
  let nearestLonLat = null; // cached nearest radar site [lon, lat] (see recomputeNearest)
  let windowMs = null; // [startMs, endMs]
  let cursorMs = null;
  let stepMs = null;
  let series = null;
  // Single-slot abortable fetch: a continuous pan supersedes its own requests,
  // leaving exactly one live query. Shared with probe.js.
  const fetchSlot = createFetchSlot();
  let refetchTimer = 0;

  function setReadout(value) {
    const text = formatReadout(value, paramSpec(parameter));
    if (!text) {
      readout.hidden = true;
      readout.textContent = '';
    } else {
      readout.textContent = text;
      readout.hidden = false;
    }
  }

  // --- centre pixel colour + radar-direction line (cheap, every render) -----
  function nearestSiteLonLat(center) {
    const feats = radarSiteSource.getFeatures();
    if (!feats.length) return null;
    const proj = map.getView().getProjection();
    let best = null;
    let bestD = Infinity;
    feats.forEach((f) => {
      const g = f.getGeometry();
      if (!g) return;
      const ll = transform(g.getCoordinates(), proj, 'EPSG:4326');
      const d = getDistance(center, ll);
      if (d < bestD) { bestD = d; best = ll; }
    });
    return best;
  }

  // Recompute (and cache) the nearest radar site. Run on moveend / arm — NOT
  // per frame — since the scan over all sites is wasted work during a pan and
  // the nearest rarely changes mid-pan. updateDirection still re-aims the arrow
  // at this cached target every frame, so it tracks smoothly while panning.
  function recomputeNearest() {
    nearestLonLat = center4326 ? nearestSiteLonLat(center4326) : null;
  }

  function sampleColor() {
    const view = map.getView();
    const center = view.getCenter();
    if (!center) return;
    const pixel = map.getPixelFromCoordinate(center);
    let data = null;
    if (pixel && radarLayer.getVisible()) {
      try { data = radarLayer.getData(pixel); } catch (_) { data = null; }
    }
    if (data && data.length >= 4 && data[3] > 0) {
      centerCircle.setAttribute('fill', `rgba(${data[0]},${data[1]},${data[2]},${(data[3] / 255).toFixed(3)})`);
      centerCircle.classList.remove('empty');
    } else {
      centerCircle.setAttribute('fill', 'none');
      centerCircle.classList.add('empty');
    }
  }

  // Aim the radar-direction arrow in screen space. The head lands exactly on
  // the radar's on-screen position when within reach, and points straight at it
  // at a capped length when the radar is far off-screen — never overshooting.
  function updateDirection() {
    const target = center4326
      ? (getActiveSiteLonLat() || nearestLonLat)
      : null;
    const centerPx = map.getPixelFromCoordinate(map.getView().getCenter());
    const targetPx = target
      ? map.getPixelFromCoordinate(transform(target, 'EPSG:4326', map.getView().getProjection()))
      : null;
    if (!centerPx || !targetPx) { dirGroup.style.display = 'none'; return; }

    const dx = targetPx[0] - centerPx[0];
    const dy = targetPx[1] - centerPx[1];
    const dist = Math.hypot(dx, dy);
    if (dist < MIN_TARGET_PX) { dirGroup.style.display = 'none'; return; }

    const ux = dx / dist;
    const uy = dy / dist;
    const len = Math.min(dist, LINE_OUTER_MAX);
    const tipX = C + ux * len;
    const tipY = C + uy * len;
    const backX = C + ux * (len - ARROW_LEN);
    const backY = C + uy * (len - ARROW_LEN);
    const perpX = -uy * ARROW_HALF_W;
    const perpY = ux * ARROW_HALF_W;

    dirLine.setAttribute('x1', (C + ux * LINE_INNER).toFixed(1));
    dirLine.setAttribute('y1', (C + uy * LINE_INNER).toFixed(1));
    dirLine.setAttribute('x2', tipX.toFixed(1));
    dirLine.setAttribute('y2', tipY.toFixed(1));
    dirArrow.setAttribute('points', `${tipX.toFixed(1)},${tipY.toFixed(1)} `
      + `${(backX + perpX).toFixed(1)},${(backY + perpY).toFixed(1)} `
      + `${(backX - perpX).toFixed(1)},${(backY - perpY).toFixed(1)}`);
    dirGroup.style.display = '';
  }

  function refreshCenter4326() {
    const center = map.getView().getCenter();
    if (!center) { center4326 = null; return false; }
    const ll = transform(center, map.getView().getProjection(), 'EPSG:4326');
    const norm = normalizeLonLat(ll[0], ll[1]);
    const moved = !center4326 || center4326[0] !== norm[0] || center4326[1] !== norm[1];
    center4326 = norm;
    return moved;
  }

  function render() {
    if (!visible) return;
    sampleColor();
    updateDirection();
  }

  // --- value via EDR (windowed) --------------------------------------------
  // The aggregation rule (floor, then most-extreme-by-magnitude within the
  // cell) lives in src/edr/peaks.js and is shared with the probe chart, so this
  // readout and the Pistemittaus bar for the same point cannot drift apart.
  // This used to be a hand-copy of probe.js kept in step by a comment (#126).
  function pickFrameValue() {
    if (!series || !windowMs || !stepMs || cursorMs == null) return null;
    const { floor } = paramSpec(parameter);
    const startMs = windowMs[0];
    const idx = frameIndexAt(cursorMs, startMs, stepMs);
    if (idx == null || idx < 0 || idx >= FRAME_COUNT) return null;
    return peaksByFrame(series, startMs, stepMs, FRAME_COUNT, floor)[idx];
  }

  function updateValue() {
    setReadout(pickFrameValue());
  }

  async function refetch() {
    if (!visible || !collection || !center4326 || !windowMs) {
      setReadout(null);
      return;
    }
    const startISO = new Date(windowMs[0]).toISOString();
    const endISO = new Date(windowMs[1]).toISOString();
    const [lon, lat] = center4326;
    const r = await fetchSlot.run((signal) => fetchSeries(collection, parameter, lon, lat, startISO, endISO, z, signal));
    // undefined = superseded by a newer request (the common case mid-pan).
    // Leave the readout showing the previous value rather than blanking it —
    // a newer request is already on its way.
    if (!r) return;
    if (!r.ok) {
      series = null;
      setReadout(null);
      return;
    }
    series = r.data;
    updateValue();
  }

  function scheduleRefetch() {
    if (refetchTimer) clearTimeout(refetchTimer);
    refetchTimer = window.setTimeout(() => { refetchTimer = 0; refetch(); }, 200);
  }

  // --- map wiring (attached once; cheap no-op while hidden) ------------------
  map.on('postrender', () => {
    if (!visible) return;
    // Colour + direction line follow the centre live (cheap, local). When the
    // centre actually moves, also (re)arm the debounced dBZ refetch — a fast
    // continuous pan keeps resetting the 200 ms timer, so the network query
    // only fires once the user pauses/stops.
    const moved = refreshCenter4326();
    // One-time catch-up: if the radar-site features only finished loading after
    // the tool was armed, pick up the nearest now. Steady-state recompute is on
    // moveend, so this is a cheap no-op once a target exists.
    if (nearestLonLat == null && !getActiveSiteLonLat()) recomputeNearest();
    render();
    if (moved) scheduleRefetch();
  });

  map.on('moveend', () => {
    if (!visible) return;
    // Guaranteed final refetch after a move. Not gated on refreshCenter4326()'s
    // "moved" result: the postrender handler above already advanced center4326
    // during the pan, so by moveend the delta is gone and a gate would never
    // trip. fetchSeries is cached, so a no-op move is cheap.
    refreshCenter4326();
    recomputeNearest();
    scheduleRefetch();
  });

  return {
    show() {
      if (visible) return;
      visible = true;
      overlay.style.display = '';
      refreshCenter4326();
      recomputeNearest();
      render();
      refetch();
      // Force a fresh postrender so the centre colour samples a current frame
      // even if the map is otherwise idle at the moment of arming.
      map.render();
    },
    hide() {
      if (!visible) return;
      visible = false;
      overlay.style.display = 'none';
      fetchSlot.abort();
      if (refetchTimer) { clearTimeout(refetchTimer); refetchTimer = 0; }
    },
    isVisible: () => visible,
    // Track the EDR collection/parameter/elevation for whatever radar product
    // is currently displayed. The dedupe rule is shared with probe.js — several
    // WMS layers resolve to the same EDR query, and refetching for those would
    // be pure waste.
    setActiveLayer(wmslayer, opts = {}) {
      const t = resolveEdrTarget(wmslayer, { z: opts.z });
      if (sameTarget(t, { collection, parameter, z })) return;
      collection = t.collection;
      parameter = t.parameter;
      z = t.z;
      series = null;
      if (visible) refetch();
    },
    // Refetch only when the window shifts; a cursor move inside the same window
    // just repicks the frame from the cached series. Window math shared with
    // probe.js so the two ask the server for identical spans.
    setCursor(cursorTimeMs, windowStartMs, step) {
      cursorMs = cursorTimeMs;
      stepMs = step;
      const w = frameWindow(windowStartMs, step);
      const changed = !sameWindow(windowMs, w);
      windowMs = w;
      if (!visible) return;
      if (changed) refetch();
      else updateValue();
    },
  };
}
